import type { AssessmentStatus } from "../types/assessment.js";
import type { ActingParty, PersonInvolvement } from "../types/involvement.js";
import type { PersonAction } from "../types/personActions.js";
import type { UserId } from "../types/user.js";
import { isAssessmentApprover } from "./isAssessmentApprover.js";
import { isAssessmentStatusUnderWay } from "./isAssessmentStatusUnderWay.js";

/**
 * Whether a party may read what an assessment says: its responses, its
 * comments and why a submission was returned.
 *
 * Whoever works on the assessment reads it in any state: its owner while they
 * may still assess the person, and the approver named for the owner's work on
 * a definitive assessment, the only kind anybody approves. Anybody else reads
 * it only once it is finished, and only where their reach over the person
 * conveys `read_assessments`: work still under way is its assessor's and its
 * approver's until it is finished. A party holding several parts gets the
 * widest.
 *
 * One statement for the server's gate and the page's offer, so a page never
 * links to contents the server withholds.
 */
export const mayReadAssessmentContents = (
  actions: readonly PersonAction[],
  involvements: readonly Pick<
    PersonInvolvement,
    "kind" | "party" | "approvesUserId" | "endedAt" | "expiresAt"
  >[],
  party: Pick<ActingParty, "userId" | "organisationId">,
  assessment: {
    ownerUserId: UserId;
    status: AssessmentStatus;
    definitive: boolean;
  },
  now: Date = new Date(),
): boolean => {
  const owns =
    assessment.ownerUserId === party.userId && actions.includes("assess");
  const approves =
    assessment.definitive &&
    actions.includes("approve") &&
    isAssessmentApprover(involvements, party, assessment, now);

  if (owns || approves) {
    return true;
  }

  return (
    !isAssessmentStatusUnderWay(assessment.status) &&
    actions.includes("read_assessments")
  );
};
